import { Box, Grid, Paper, Typography, Tabs, Tab } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import WeeklyRevenueChart from "./DailyRevenueBarChart";
import MonthlyActivityCard from "./MonthlyActivityDonut";
import RevenueChart from "./RevenueTrendChart";
import WSDataGridNew, {
  IColumns,
} from "../../../Components/DataGrid/WSDataGridNew";
import { IHeaderRows } from "../../../Components/Filter/WSHeaderFilter";
import { breadCrumbsStore } from "../../../Components/BreadCrumbs/BreadCrumbsListner";
import { getLocalValue, LoacalVariables, updateMainHeader } from "../../../commonFunctions/CommonFunctions";
import WSDashboardCard from "../../../Components/CardListing/WSDashboardCard";
import { WSIcons } from "../../../commonFunctions/iIcons";
import { TimePeriodSelector } from "../../../Components/TimePeriodSelector/TimePeriodSelector";
import axios from 'axios';
import RevenueRecentOrders from "./RevenueRecentOrders";
import AddIcon from "@mui/icons-material/Add";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import http from "../../../commonFunctions/httpClient";
import { formatRupees, getBookingDate, isPaidBooking } from "./revenueData";

// Periods for the cards; "This month" is the default
const PERIODS = ["Today", "This week", "This month", "Last month", "This year", "All time"];

// [start, end) of a period in local time; null = no limit
const getPeriodRange = (period: string): [Date | null, Date | null] => {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (period) {
    case "Today":
      return [startOfToday, null];
    case "This week": {
      // Week starts on Monday
      const start = new Date(startOfToday);
      start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
      return [start, null];
    }
    case "This month":
      return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    case "Last month":
      return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    case "This year":
      return [new Date(now.getFullYear(), 0, 1), null];
    default:
      return [null, null];
  }
};

interface SummaryCounts {
  totalRevenue: number;
  paymentsReceived: number;
  homeCollectionCharges: number;
  totalServiceFee: number;
}


// Define the API response type
interface RecentOrder {
  id: string;
  labId: string;
  collectionType: string;
  totalAmount: number;
  orderNumber: string;
  paymentMode: string;
  paymentStatus: string;
  status: string;
  createdAt: string;
  collectionFee: number;
  serviceFee: number;
  orderdate: string;
}
export default function RevenuePaymentsDashboard() {
  const [summaryCounts, setSummaryCounts] = useState<SummaryCounts>({
    totalRevenue: 0,
    paymentsReceived: 0,
    homeCollectionCharges: 0,
    totalServiceFee: 0
  });
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [labId, setLabId] = useState<string>("");
  const [period, setPeriod] = useState("This month");
  const [bookings, setBookings] = useState<any[]>([]);

    useEffect(() => {
      const userId = getLocalValue(LoacalVariables.UserId);
      setLabId(userId || "");
    }, []);
  
  // Cards are worked out from every booking of the lab (the dashboard summary only has the
  // latest few orders and an all-time total, so it can't follow the period filter)
  const fetchSummaryCounts = useCallback(async (): Promise<void> => {
    if (!labId) {
      setSummaryLoading(false);
      return;
    }
    try {
      const response: any = await http.get(`lab/LabBooking/lab/${labId}`);
      setBookings(Array.isArray(response?.data) ? response.data : []);
    } catch (err: any) {
      console.error("Summary fetch error:", err);
      setBookings([]);
    } finally {
      setSummaryLoading(false);
    }
  }, [labId]);

  useEffect(() => {
    const [start, end] = getPeriodRange(period);
    const paid = bookings.filter((booking) => {
      if (!isPaidBooking(booking)) return false;
      const date = getBookingDate(booking);
      if (isNaN(date.getTime())) return !start && !end;
      return (!start || date >= start) && (!end || date < end);
    });
    const sum = (field: string) => paid.reduce((total, booking) => total + (Number(booking?.[field]) || 0), 0);
    setSummaryCounts({
      totalRevenue: sum("totalAmount"),
      paymentsReceived: paid.length,
      homeCollectionCharges: sum("collectionFee"),
      totalServiceFee: sum("serviceFee"),
    });
  }, [bookings, period]);

  useEffect(() => {
    fetchSummaryCounts();
  }, [fetchSummaryCounts]);

  
  return (
    <Grid sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              borderRadius: "16px",
              border: "1px solid #E5E5E5",
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={10}>
                <Typography variant="h6" style={{ color: "#022219", fontWeight: 400 }}>
                  Revenue & Payments
                </Typography>
                <Typography variant="caption" style={{ color: "#828584", fontWeight: 400 }}>
                  Track payments, revenue trends, and manage transactions
                </Typography>
              </Grid>
              <Grid item xs={12} sm={2}>
                <TimePeriodSelector selectedPeriod={period} options={PERIODS} onChange={setPeriod} />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Dashboard Cards - Now populated with real data */}
      <Grid item sm={3}>
          <WSDashboardCard
            title="Total Revenue"
            value={summaryLoading ? "…" : formatRupees(summaryCounts.totalRevenue)}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Payments Received"
            value={summaryLoading ? "…" : summaryCounts.paymentsReceived.toString()}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.InStockIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Home Collection Charges"
            value={summaryLoading ? "…" : formatRupees(summaryCounts.homeCollectionCharges)}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<WSIcons.OutOfStockIcon sx={{ color: "red" }} />}
          />
        </Grid>
        <Grid item sm={3}>
          <WSDashboardCard
            title="Total Service Fee"
            value={summaryLoading ? "…" : formatRupees(summaryCounts.totalServiceFee)}
            percentage="+2.5%"
            bottomIcon={<TrendingUpIcon />}
            icon={<ShoppingCartIcon sx={{ color: "#098963" }} />}
          />
        </Grid>
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} mt={2}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, borderRadius: "16px", border: "1px solid #E5E5E5" }}>
            <RevenueChart bookings={bookings} loading={summaryLoading} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, borderRadius: "16px", border: "1px solid #E5E5E5" }}>
            <MonthlyActivityCard />
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={3} mt={2}>
        <Grid item xs={12}>
          <Paper sx={{ mt: 3, p: 2, borderRadius: "16px", border: "1px solid #E5E5E5" }}>
            <Typography fontWeight={600}>Daily Revenue This Week</Typography>
            <WeeklyRevenueChart />
          </Paper>
        </Grid>
      </Grid>
<Grid container spacing={2}>
  <Grid item xs={12}>
    <RevenueRecentOrders/>
  </Grid>
</Grid>
     
    </Grid>
  );
}
